/**
 * Centralized configuration for all quizlet versions
 * To add a new version:
 * 1. Add a new configuration object to the versions array below with its UUID
 * 2. That's it! Routes and components will be automatically generated
 */

import {
  ROUTE_QUESTIONNAIRE_V2_QUESTION,
} from '@/constants/routes';

export interface QuizletVersionConfig {
  version: string;        // Identifier for this quizlet (e.g., 'v2', 'v4') - used for feature flags
  uuid: string;           // The questionnaire UUID in the backend database
  basePath: string;       // URL path for this version (e.g., '/quizlet/v2')
  enabled: boolean;       // Whether this version is currently active
}

// Define all quizlet versions here
// NOTE: These are only for quizlet versions that use the QuestionnaireV2 system
// v1 uses completely different components and is not included here
export const QUIZLET_VERSIONS: QuizletVersionConfig[] = [
  {
    version: 'v2',
    uuid: '95f015ff-b4ec-410b-963b-12b86c9e1825',
    basePath: '/quizlet/v2',
    enabled: true,
  },
  {
    version: 'v4',
    uuid: '52e7d175-ec28-4b7a-b966-e49428110636',
    basePath: '/quizlet/v4',
    enabled: true,
  },
  {
    version: 'v5',
    uuid: '0ab24bfa-e31a-4618-9a00-759ef7fdebf2',
    basePath: '/quizlet/v5',
    enabled: true,
  }
];

// Helper to get config by version
export const getQuizletConfig = (version: string): QuizletVersionConfig | undefined => {
  return QUIZLET_VERSIONS.find(config => config.version === version);
};

// Helper to get config by UUID
export const getQuizletConfigByUUID = (uuid: string): QuizletVersionConfig | undefined => {
  return QUIZLET_VERSIONS.find(config => config.uuid === uuid);
};

// Get all enabled versions
export const getEnabledQuizletVersions = (): QuizletVersionConfig[] => {
  return QUIZLET_VERSIONS.filter(config => config.enabled && config.uuid);
};

// Build API version map for questionnaireV2.service.ts - all quizlets use v2
export const buildQuizletAPIVersionMap = (): Record<string, 'v1' | 'v2'> => {
  const map: Record<string, 'v1' | 'v2'> = {};
  QUIZLET_VERSIONS.forEach(config => {
    if (config.uuid) {
      map[config.uuid] = 'v2';  // All quizlets use v2 API for anonymous sessions
    }
  });
  return map;
};

/**
 * Determines the appropriate question route based on questionnaire or current pathname
 * This function consolidates routing logic using the centralized configuration
 */
export function getQuestionnaireRoute(questionnaire?: { id?: string; slug?: string } | null, pathname?: string): string {
  // Use provided pathname or current location
  const currentPath = pathname || (typeof window !== 'undefined' ? window.location.pathname : '');

  // Check pathname against all configured quizlet routes dynamically
  for (const config of QUIZLET_VERSIONS) {
    if (config.enabled && config.basePath && currentPath.includes(config.basePath)) {
      // Build the question route dynamically from the basePath
      return `${config.basePath}/questionnaire/:questionSlug`;
    }
  }

  // If we have a questionnaire ID, check against our configs
  if (questionnaire?.id) {
    const config = getQuizletConfigByUUID(questionnaire.id);
    if (config && config.enabled) {
      // Build the question route dynamically from the config
      return `${config.basePath}/questionnaire/:questionSlug`;
    }
  }

  // Fallback: check slug for quizlet versions dynamically
  if (questionnaire?.slug) {
    // Check each config to see if the slug matches
    for (const config of QUIZLET_VERSIONS) {
      if (config.enabled && questionnaire.slug.startsWith(`quizlet${config.version === 'v2' ? '' : `-${config.version}`}`)) {
        return `${config.basePath}/questionnaire/:questionSlug`;
      }
    }
  }

  // Default to standard questionnaire route
  return ROUTE_QUESTIONNAIRE_V2_QUESTION;
}
